import { InsertDriveFileOutlined, LayersOutlined } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@mui/material";
import ChatInputButton from "../components/ChatInputButton";
import ChatSettingsTooltip from "src/reusable/components/ChatSettingsTooltip";
import DatasetSelectorModal from "./DatasetSelectorModal";
import { truncateMiddle } from "src/reusable/Functions";

type ChatDatasetSelectionBtnProps = {
  handleSelectedOptions: (datasets: any[]) => void;
  defaultSelectedDatasets?: any[];
  onlyIcon?: boolean;
};

const ChatDatasetSelectionBtn = ({
  handleSelectedOptions,
  defaultSelectedDatasets,
  onlyIcon = false,
}: ChatDatasetSelectionBtnProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDatasets, setSelectedDatasets] = useState<any[]>([]);

  // Memoize button label
  const buttonLabel = useMemo(() => {
    switch (selectedDatasets?.length) {
      case 0:
        return "Search Dataset";
      case 1:
        return truncateMiddle(selectedDatasets[0]?.name, 16);
      default:
        return `${selectedDatasets?.length} Datasets`;
    }
  }, [selectedDatasets]);

  const handleClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleModalConfirm = useCallback(
    (datasets: any[]) => {
      setSelectedDatasets(datasets);
      handleSelectedOptions(datasets);
    },
    [handleSelectedOptions],
  );

  // Handle default selected datasets
  useEffect(() => {
    if (defaultSelectedDatasets && defaultSelectedDatasets.length >= 0) {
      setSelectedDatasets(defaultSelectedDatasets);
    }
  }, [defaultSelectedDatasets]);

  return (
    <>
      <ChatSettingsTooltip
        title="Select data for AtomAssist to reference when answering questions."
        placement="top"
        enterDelay={200}
        arrow
      >
        {onlyIcon ? (
          <ChatInputButton
            icon={
              selectedDatasets?.length > 0 ? (
                <InsertDriveFileOutlined />
              ) : (
                <LayersOutlined />
              )
            }
            text={buttonLabel}
            selected={selectedDatasets?.length > 0}
            onClick={handleClick}
            tooltip="Select data for AtomAssist to reference when answering questions."
            tooltipPlacement="top-start"
            data-testid="aa-ds-selector-btn"
            data-attr="assistant:btn:open-dataset-selector"
          />
        ) : (
          <Button
            aria-label="Select dataset"
            variant="text"
            size="small"
            color={selectedDatasets?.length ? "primary" : "inherit"}
            onClick={handleClick}
            data-testid="aa-ds-selector-btn"
            data-attr="assistant:btn:open-dataset-selector"
            startIcon={
              selectedDatasets?.length > 0 ? (
                <InsertDriveFileOutlined />
              ) : (
                <LayersOutlined />
              )
            }
            sx={{
              textTransform: "none",
              fontSize: "0.8125rem",
              px: 1,
              py: 0.25,
              "& .MuiButton-startIcon": {
                mr: 0.5,
                "& svg": { fontSize: 18 },
              },
            }}
          >
            {buttonLabel}
          </Button>
        )}
      </ChatSettingsTooltip>

      <DatasetSelectorModal
        open={modalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        defaultSelectedDatasets={defaultSelectedDatasets}
        data-testid="aa-ds-selector-modal"
      />
    </>
  );
};

export default ChatDatasetSelectionBtn;
